import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	CurrencyDisplay,
	FormBoxInput,
	MaterialIcons,
	PrimaryButton,
	RegularButtonWrapper,
	TertiaryButton,
	FormInput,
	InfoTooltip,
	Collapse,
	COLORS,
} from '@monthio/react-components'
import styled from 'styled-components'
import { FieldArray, FieldArrayRenderProps, Formik } from 'formik'
import * as Yup from 'yup'
import { TransactionsTableWrapper } from './expenseEstimate'
import { getExpenseEstimateTitle } from './getExpenseEstimateTitle'
import i18next from 'i18next'
import { TransactionsTable } from '#modules/Budget/Components/BudgetOverview/BudgetCategorizedTransactionsTable/TransactionsTable'
import { ExpandIcon } from '../../../modulesTemp/shared/ExpandIcon'
import {
	RowsWithHeadingContainer,
	RowWithHeading,
} from '../../../modulesTemp/general/FlowWizardUtils'
import {
	ExpenseBucket,
	ExpenseEstimateFormValues,
} from '../AssetExpenses.types'
import {
	ExpenseEstimatesDto,
	PaymentFrequency,
} from '#modules/Budget/types/expense.types'
import { Transaction } from '#modules/Budget/types/budgetApi.types'

export interface AssetBudgetExpenseEstimateModalResult {
	expenseEstimates: ExpenseEstimateFormValues[]
	monthlyAmount: number
}

interface AssetBudgetExpenseEstimateModalProps {
	assetId: string
	expenseBucket: (typeof ExpenseBucket)[keyof typeof ExpenseBucket]
	onClose: (result: AssetBudgetExpenseEstimateModalResult | null) => void
	expenseEstimates: ExpenseEstimateFormValues[]
	monthlyEstimate: number
	amountModifiedByCreditPolicy: boolean
}

const StyledRegularButtonWrapper = styled(RegularButtonWrapper)`
	padding: 12px;
`
const CurrencyDisplayContainer = styled.div`
	display: flex;
`
const TooltipContainer = styled.div`
	margin-right: 5px;
`
const StyledTransactionsTableWrapper = styled(TransactionsTableWrapper)`
	max-height: 20vh;
	overflow: scroll;
`

const FlexContainer = styled.div`
	display: flex;
`

const ExpandIconWrapper = styled.div<{ $isVisible: boolean }>`
	cursor: pointer;
	visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`

const getSum = (estimates: ExpenseEstimateFormValues[]) => {
	return estimates
		.map((x) => (x.assetId != null ? x.monthlyAmount : 0))
		.reduce((a, b) => a + b, 0)
}

const validationSchema = Yup.object().shape({
	estimates: Yup.array().of(
		Yup.object().shape({
			name: Yup.string().when(['addedByUser', 'assetId'], {
				is: (addedByUser: boolean, assetId: string | null) =>
					addedByUser === true && assetId != null,
				then: (schema) =>
					schema.required(i18next.t('validation-fieldRequired')),
			}),
		}),
	),
})

const ExpenseRow = ({
	estimate,
	index,
	setFieldValue,
	assetId,
	expenseBucket,
	isMaintenanceExpenseBucket,
	expanded,
	handleExpanded,
}: {
	estimate: ExpenseEstimateFormValues
	index: number
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
	assetId: string
	expenseBucket: (typeof ExpenseBucket)[keyof typeof ExpenseBucket]
	expanded?: boolean
	isMaintenanceExpenseBucket?: boolean
	handleExpanded: () => void
}) => {
	const { t } = useTranslation()

	const occurrencesCount = estimate.underlyingChain?.transactions.length ?? 0

	const heading: string = getExpenseEstimateTitle(estimate)

	const handleSelect = (isSelected: boolean) => {
		setFieldValue(`estimates[${index}].assetId`, isSelected ? assetId : null)
	}

	return (
		<RowWithHeading
			heading={
				estimate.addedByUser ? (
					<FormInput
						id={`estimates[${index}].name`}
						size="small"
						placeholder={t('assetExpenses-budgetModal-addedByUser-placeholder')}
					/>
				) : (
					heading
				)
			}
			subheading={
				occurrencesCount
					? t('assetExpenses-budgetModal-occurences-label', {
							count: occurrencesCount,
						})
					: undefined
			}
			key={estimate.id}
			isSelected={estimate.assetId != null}
			onSelect={!isMaintenanceExpenseBucket ? handleSelect : undefined}
		>
			<FlexContainer>
				<FormBoxInput
					id={`estimates[${index}].monthlyAmount`}
					value={Math.abs(Math.round(estimate.monthlyAmount))}
					currencyInputProps={{
						allowNegativeValue: false,
						allowDecimals: false,
					}}
					onChange={(event) =>
						setFieldValue(
							`estimates[${index}].monthlyAmount`,
							-parseInt(event.target.value ?? 0),
						)
					}
				/>
				<ExpandIconWrapper
					$isVisible={occurrencesCount > 0}
					onClick={handleExpanded}
				>
					<ExpandIcon
						expanded={expanded ?? false}
						color={COLORS.TEXT_COLORS.DEFAULT}
					/>
				</ExpandIconWrapper>
			</FlexContainer>
		</RowWithHeading>
	)
}

const AddEstimateButton = ({ onClick }: { onClick: () => void }) => {
	const { t } = useTranslation()

	return (
		<StyledRegularButtonWrapper>
			<TertiaryButton type="button" onClick={onClick}>
				<MaterialIcons.Add />
				{t('assetExpenses-budgetModal-addMore-button')}
			</TertiaryButton>
		</StyledRegularButtonWrapper>
	)
}

export const AssetBudgetExpenseEstimateModal: React.FC<
	AssetBudgetExpenseEstimateModalProps
> = ({
	assetId,
	expenseEstimates,
	expenseBucket,
	onClose,
	monthlyEstimate,
	amountModifiedByCreditPolicy,
}) => {
	const { t } = useTranslation()
	const [touched, setTouched] = useState<boolean>(false)

	const handleAddEstimate = (arrayHelpers: FieldArrayRenderProps) => {
		const newEstimate: ExpenseEstimateFormValues = {
			id: '',
			addedByUser: true,
			name: '',
			assetId,
			monthlyAmount: 0,
			possibleAssetIds: [assetId],
			type: PaymentFrequency.Unknown,
		}
		arrayHelpers.push(newEstimate)
	}

	const handleSubmit = (values: ExpenseEstimatesDto) =>
		onClose({
			expenseEstimates: values.estimates,
			monthlyAmount: touched ? getSum(values.estimates) : monthlyEstimate,
		})

	const isMaintenanceExpenseBucket = [
		'housingMaintenance',
		'carMaintenance',
	].includes(expenseBucket)

	const displayTotal = (values: {
		estimates: ExpenseEstimateFormValues[]
	}): number => {
		if (touched) return Math.abs(Math.round(getSum(values.estimates)))

		return Math.abs(monthlyEstimate)
	}

	const shouldRenderCreditPolicyTooltip = (
		estimates: ExpenseEstimateFormValues[],
	) => {
		return (
			!touched &&
			amountModifiedByCreditPolicy &&
			monthlyEstimate !== getSum(estimates)
		)
	}

	return (
		<Formik
			initialValues={{
				estimates: expenseEstimates as ExpenseEstimateFormValues[],
			}}
			validationSchema={validationSchema}
			validateOnMount={true}
			onSubmit={handleSubmit}
		>
			{({ values, submitForm, setFieldValue, isValid }) => {
				const estimates = isMaintenanceExpenseBucket
					? values.estimates[0]
						? [values.estimates[0]]
						: []
					: values.estimates
				return (
					<>
						<RowsWithHeadingContainer>
							<FieldArray
								name="estimates"
								render={(arrayHelpers) => (
									<>
										{estimates.map((estimate, i) => (
											<div key={`ESTIMATE::${i}`}>
												<ExpenseRowWithUnderlyingChain
													estimate={estimate}
													index={i}
													setFieldValue={(field, value, shouldValidate) => {
														setTouched(true)
														setFieldValue(field, value, shouldValidate)
													}}
													assetId={assetId}
													expenseBucket={expenseBucket}
													transactions={estimate.underlyingChain?.transactions}
													isMaintenanceExpenseBucket={
														isMaintenanceExpenseBucket
													}
												/>
											</div>
										))}
										<AddEstimateButton
											onClick={() => {
												setTouched(true)
												handleAddEstimate(arrayHelpers)
											}}
										/>
									</>
								)}
							/>
						</RowsWithHeadingContainer>

						<RowWithHeading
							heading={`${t('assetExpenses-budgetModal-monthlyTotal-label')} ${t(
								`assetExpenses-budgetBucket-${expenseBucket}`,
							).toLocaleLowerCase()}`}
						>
							<CurrencyDisplayContainer>
								{shouldRenderCreditPolicyTooltip(values.estimates) ? (
									<TooltipContainer>
										<InfoTooltip
											tooltip={t('assetExpenses-valueModifiedByCreditpolicy')}
											variant="info"
										/>
									</TooltipContainer>
								) : null}
								<CurrencyDisplay amount={displayTotal(values)} />
							</CurrencyDisplayContainer>
						</RowWithHeading>

						<StyledRegularButtonWrapper>
							<PrimaryButton onClick={submitForm} disabled={!isValid}>
								{t('btn-save')}
							</PrimaryButton>
						</StyledRegularButtonWrapper>
					</>
				)
			}}
		</Formik>
	)
}

const ExpenseRowWithUnderlyingChain = ({
	index,
	assetId,
	estimate,
	expenseBucket,
	transactions,
	setFieldValue,
	isMaintenanceExpenseBucket,
}: {
	index: number
	assetId: string
	estimate: ExpenseEstimateFormValues
	expenseBucket: (typeof ExpenseBucket)[keyof typeof ExpenseBucket]
	transactions?: Transaction[]
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
	isMaintenanceExpenseBucket: boolean
}) => {
	const [expanded, setExpanded] = useState(false)
	const handleClick = () => {
		setExpanded((prev) => !prev)
	}

	return (
		<>
			<ExpenseRow
				key={`ESTIMATE::${index}`}
				estimate={estimate}
				index={index}
				setFieldValue={setFieldValue}
				assetId={assetId}
				expenseBucket={expenseBucket}
				expanded={expanded}
				handleExpanded={handleClick}
				isMaintenanceExpenseBucket={isMaintenanceExpenseBucket}
			/>
			<Collapse in={expanded} timeout="auto">
				<StyledTransactionsTableWrapper>
					<TransactionsTable
						transactions={transactions}
						columnWidths={['20%', '40%', '40%']}
					/>
				</StyledTransactionsTableWrapper>
			</Collapse>
		</>
	)
}
